import {
  definePreset,
  DEGREES_SCHEMA,
  DIRECTION_SCHEMA,
  EDGE_DIRECTION_SCHEMA,
  DISTANCE_SCHEMA,
  kf,
  rad,
  samplePath,
  setStatic,
  SLIDE_VECTORS,
  timing,
  WIPE_TO_REVEAL_DIR,
} from "./helpers";

import { fragmentEffect } from "./exit";

import type { AnimationPreset } from "./types";

export const entrancePresets: AnimationPreset[] = [
  definePreset(
    "assemble",
    "Assemble",
    "entrance",
    {
      params: { pieces: 6, spread: 1, gravity: 1, twist: 1 },
      schema: [
        { key: "pieces", label: "Pieces", min: 2, max: 10, step: 1 },
        { key: "spread", label: "Spread", min: 0.2, max: 3, step: 0.1 },
        { key: "gravity", label: "Gravity", min: 0, max: 3, step: 0.1 },
        { key: "twist", label: "Twist", min: 0, max: 3, step: 0.1 },
      ],
      duration: 1200,
      easing: "power2.out",
    },
    // the explode flight run in reverse: pieces fly in and lock together
    fragmentEffect(1, 0),
  ),
  definePreset(
    "sketchIn",
    "Sketch In",
    "entrance",
    {
      params: { passes: 3, jitter: 1 },
      schema: [
        { key: "passes", label: "Passes", min: 1, max: 4, step: 1 },
        { key: "jitter", label: "Jitter", min: 0, max: 2, step: 0.1 },
      ],
      duration: 1400,
      easing: "power1.out",
    },
    // sketchy build-up: the stroke draws on while extra roughness settles to
    // rest and the rough seed re-rolls once per pass — reads as several
    // overlapping construction strokes converging on the final shape
    (tl, target, _element, cfg) => {
      const passes = Math.max(1, Math.round(cfg.params.passes ?? 3));
      const jitter = cfg.params.jitter ?? 1;
      tl.fromTo(
        target,
        { drawProgress: 0, opacity: 0.3 },
        { drawProgress: 1, opacity: 1, ...timing(cfg) },
        0,
      );
      tl.fromTo(
        target,
        { roughnessDelta: jitter * 2 },
        { roughnessDelta: 0, ...timing(cfg) },
        0,
      );
      // linear on purpose — the renderer floors seedShift, so a linear ramp
      // gives equal-length passes regardless of the clip's easing
      tl.fromTo(
        target,
        { seedShift: 1 },
        { seedShift: passes + 1 - 1e-6, ...timing(cfg), ease: "none" },
        0,
      );
    },
  ),
  definePreset(
    "fade",
    "Fade",
    "entrance",
    {
      params: { from: 0, to: 100 },
      schema: [
        { key: "from", label: "From %", min: 0, max: 100, step: 5 },
        { key: "to", label: "To %", min: 0, max: 100, step: 5 },
      ],
    },
    (tl, target, _element, cfg) => {
      tl.fromTo(
        target,
        { opacity: (cfg.params.from ?? 0) / 100 },
        { opacity: (cfg.params.to ?? 100) / 100, ...timing(cfg) },
        0,
      );
    },
  ),
  definePreset(
    "slideIn",
    "Slide In",
    "entrance",
    {
      params: { direction: 0, distance: 200 },
      schema: [DIRECTION_SCHEMA, DISTANCE_SCHEMA],
      easing: "power3.out",
    },
    (tl, target, _element, cfg) => {
      const [vx, vy] =
        SLIDE_VECTORS[cfg.params.direction ?? 0] ?? SLIDE_VECTORS[0];
      const d = cfg.params.distance ?? 200;
      tl.fromTo(
        target,
        { dx: vx * d, dy: vy * d, opacity: 0 },
        { dx: 0, dy: 0, opacity: 1, ...timing(cfg) },
        0,
      );
    },
  ),
  definePreset(
    "zoomIn",
    "Zoom In",
    "entrance",
    {
      params: { from: 0 },
      schema: [
        { key: "from", label: "From scale", min: 0, max: 1, step: 0.05 },
      ],
      easing: "power3.out",
    },
    (tl, target, _element, cfg) => {
      const from = cfg.params.from ?? 0;
      tl.fromTo(
        target,
        { scaleX: from, scaleY: from, opacity: 0 },
        { scaleX: 1, scaleY: 1, opacity: 1, ...timing(cfg) },
        0,
      );
    },
  ),
  definePreset(
    "bounceIn",
    "Bounce In",
    "entrance",
    {
      params: { from: 0.3, intensity: 1 },
      schema: [
        { key: "from", label: "From scale", min: 0, max: 1, step: 0.05 },
        { key: "intensity", label: "Overshoot", min: 0.1, max: 3, step: 0.1 },
      ],
    },
    (tl, target, _element, cfg) => {
      const from = cfg.params.from ?? 0.3;
      const k = cfg.params.intensity ?? 1;
      const scale = [
        from,
        ...[1.05, 0.9, 1.03, 0.97].map((v) => 1 + (v - 1) * k),
        1,
      ];
      tl.to(
        target,
        kf(
          cfg,
          { scaleX: scale, scaleY: scale, opacity: [0, 1, 1, 1, 1, 1] },
          "sine.out",
        ),
        0,
      );
    },
  ),
  definePreset(
    "backIn",
    "Back In",
    "entrance",
    {
      params: { from: 0.4 },
      schema: [
        { key: "from", label: "From scale", min: 0, max: 1, step: 0.05 },
      ],
      easing: "back.out",
    },
    (tl, target, _element, cfg) => {
      const from = cfg.params.from ?? 0.4;
      tl.fromTo(
        target,
        { scaleX: from, scaleY: from, opacity: 0 },
        { scaleX: 1, scaleY: 1, opacity: 1, ...timing(cfg) },
        0,
      );
    },
  ),
  definePreset(
    "rotateIn",
    "Rotate In",
    "entrance",
    {
      params: { degrees: -180, scale: 0.5 },
      schema: [
        DEGREES_SCHEMA,
        { key: "scale", label: "From scale", min: 0, max: 1, step: 0.05 },
      ],
      easing: "power2.out",
    },
    (tl, target, _element, cfg) => {
      const from = cfg.params.scale ?? 0.5;
      tl.fromTo(
        target,
        {
          angleDelta: rad(cfg.params.degrees ?? -180),
          scaleX: from,
          scaleY: from,
          opacity: 0,
        },
        { angleDelta: 0, scaleX: 1, scaleY: 1, opacity: 1, ...timing(cfg) },
        0,
      );
    },
  ),
  definePreset(
    "flipInX",
    "Flip In X",
    "entrance",
    {
      params: { from: 0 },
      schema: [
        { key: "from", label: "From scale", min: 0, max: 1, step: 0.05 },
      ],
      easing: "power2.out",
    },
    (tl, target, _element, cfg) => {
      tl.fromTo(
        target,
        { scaleY: cfg.params.from ?? 0, opacity: 0 },
        { scaleY: 1, opacity: 1, ...timing(cfg) },
        0,
      );
    },
  ),
  definePreset(
    "flipInY",
    "Flip In Y",
    "entrance",
    {
      params: { from: 0 },
      schema: [
        { key: "from", label: "From scale", min: 0, max: 1, step: 0.05 },
      ],
      easing: "power2.out",
    },
    (tl, target, _element, cfg) => {
      tl.fromTo(
        target,
        { scaleX: cfg.params.from ?? 0, opacity: 0 },
        { scaleX: 1, opacity: 1, ...timing(cfg) },
        0,
      );
    },
  ),
  definePreset(
    "rollIn",
    "Roll In",
    "entrance",
    {
      params: { distance: 200, degrees: 120 },
      schema: [
        DISTANCE_SCHEMA,
        { key: "degrees", label: "Roll °", min: 10, max: 360, step: 5 },
      ],
      easing: "power2.out",
    },
    (tl, target, _element, cfg) => {
      tl.fromTo(
        target,
        {
          dx: -(cfg.params.distance ?? 200),
          angleDelta: rad(-(cfg.params.degrees ?? 120)),
          opacity: 0,
        },
        { dx: 0, angleDelta: 0, opacity: 1, ...timing(cfg) },
        0,
      );
    },
  ),
  definePreset(
    "wipeIn",
    "Wipe In",
    "entrance",
    {
      params: { direction: 1 },
      schema: [EDGE_DIRECTION_SCHEMA],
      easing: "none",
    },
    (tl, target, _element, cfg) => {
      setStatic(tl, target, {
        revealDir:
          WIPE_TO_REVEAL_DIR[cfg.params.direction ?? 1] ??
          WIPE_TO_REVEAL_DIR[1],
      });
      tl.fromTo(
        target,
        { revealProgress: 0 },
        { revealProgress: 1, ...timing(cfg) },
        0,
      );
    },
  ),
  definePreset(
    "irisIn",
    "Iris In",
    "entrance",
    { easing: "power1.inOut" },
    // radial wipe: a circle grows from the center until the corners clear
    (tl, target, _element, cfg) => {
      setStatic(tl, target, { revealDir: 4 });
      tl.fromTo(
        target,
        { revealProgress: 0 },
        { revealProgress: 1, ...timing(cfg) },
        0,
      );
    },
  ),
  definePreset(
    "swingIn",
    "Swing In",
    "entrance",
    {
      params: { degrees: 60 },
      schema: [
        { key: "degrees", label: "Degrees", min: 10, max: 120, step: 5 },
      ],
      duration: 1200,
    },
    (tl, target, _element, cfg) => {
      const d = cfg.params.degrees ?? 60;
      tl.to(
        target,
        kf(
          cfg,
          {
            angleDelta: [-d, d * 0.3, -d * 0.15, d * 0.05, 0].map(rad),
            opacity: [0, 1, 1, 1, 1],
          },
          "sine.out",
        ),
        0,
      );
    },
  ),
  definePreset(
    "spiralIn",
    "Spiral In",
    "entrance",
    {
      params: { distance: 200, turns: 1, scale: 0.4 },
      schema: [
        DISTANCE_SCHEMA,
        { key: "turns", label: "Turns", min: 0.5, max: 3, step: 0.5 },
        { key: "scale", label: "From scale", min: 0, max: 1, step: 0.05 },
      ],
      duration: 1400,
      easing: "power2.out",
    },
    (tl, target, _element, cfg) => {
      const r0 = cfg.params.distance ?? 200;
      const turns = cfg.params.turns ?? 1;
      const from = cfg.params.scale ?? 0.4;
      const { dx, dy } = samplePath((t) => {
        const r = r0 * (1 - t);
        const a = turns * 2 * Math.PI * t;
        return [r * Math.cos(a), r * Math.sin(a)];
      });
      tl.to(
        target,
        kf(cfg, {
          dx,
          dy,
          opacity: [0, 1],
          scaleX: [from, 1],
          scaleY: [from, 1],
        }),
        0,
      );
    },
  ),
  definePreset(
    "blurIn",
    "Blur In",
    "entrance",
    {
      params: { blur: 12 },
      schema: [{ key: "blur", label: "Blur px", min: 1, max: 50, step: 1 }],
      easing: "power2.out",
    },
    (tl, target, _element, cfg) => {
      tl.fromTo(
        target,
        { blur: cfg.params.blur ?? 12, opacity: 0 },
        { blur: 0, opacity: 1, ...timing(cfg) },
        0,
      );
    },
  ),
  definePreset(
    "elasticIn",
    "Elastic In",
    "entrance",
    {
      params: { from: 0.2 },
      schema: [
        { key: "from", label: "From scale", min: 0, max: 1, step: 0.05 },
      ],
      duration: 1200,
      easing: "elastic.out",
    },
    (tl, target, _element, cfg) => {
      const from = cfg.params.from ?? 0.2;
      tl.fromTo(
        target,
        { scaleX: from, scaleY: from, opacity: 0 },
        { scaleX: 1, scaleY: 1, opacity: 1, ...timing(cfg) },
        0,
      );
    },
  ),
  definePreset(
    "jackInTheBox",
    "Jack in the Box",
    "entrance",
    {
      params: { from: 0.1 },
      schema: [
        { key: "from", label: "From scale", min: 0, max: 1, step: 0.05 },
      ],
      duration: 1000,
      easing: "power1.inOut",
    },
    // animate.css jackInTheBox: grows from a bottom-center pivot while the
    // rotation wobble decays 30° → −10° → 3° → 0
    (tl, target, _element, cfg) => {
      setStatic(tl, target, { anchorX: 0.5, anchorY: 1 });
      const from = cfg.params.from ?? 0.1;
      tl.to(
        target,
        kf(
          cfg,
          {
            angleDelta: [30, -10, 3, 0].map(rad),
            scaleX: [from, 0.7, 0.95, 1],
            scaleY: [from, 0.7, 0.95, 1],
            opacity: [0, 1, 1, 1],
          },
          "sine.out",
        ),
        0,
      );
    },
  ),
  definePreset(
    "lightSpeedIn",
    "Light Speed In",
    "entrance",
    {
      params: { direction: 1, distance: 300, degrees: 25 },
      schema: [
        {
          key: "direction",
          label: "From side",
          min: 0,
          max: 1,
          step: 1,
          options: ["Left", "Right"],
        },
        { key: "distance", label: "Distance px", min: 50, max: 1000, step: 10 },
        { key: "degrees", label: "Lean °", min: 5, max: 60, step: 1 },
      ],
      duration: 900,
      easing: "power2.out",
    },
    // animate.css lightSpeed: streaks in leaning hard, whips past vertical
    // and settles — the skew overshoot is the preset's signature
    (tl, target, _element, cfg) => {
      const sign = (cfg.params.direction ?? 1) === 0 ? -1 : 1;
      const d = (cfg.params.distance ?? 300) * sign;
      const lean = rad(cfg.params.degrees ?? 25) * sign;
      tl.to(
        target,
        kf(
          cfg,
          {
            dx: [d, 0, 0, 0],
            skewX: [-lean, lean * 0.66, -lean * 0.16, 0],
            opacity: [0, 1, 1, 1],
          },
          "sine.out",
        ),
        0,
      );
    },
  ),
  definePreset(
    "peekIn",
    "Peek In",
    "entrance",
    {
      params: { direction: 3, distance: 80 },
      schema: [
        EDGE_DIRECTION_SCHEMA,
        { key: "distance", label: "Distance px", min: 10, max: 500, step: 10 },
      ],
      duration: 800,
      easing: "power2.out",
    },
    // PowerPoint-style peek: slides in from an edge while the reveal grows
    // from that same edge — reads as emerging from behind a mask. The slide
    // origin side IS the grow-from side, so direction maps to revealDir 1:1
    // (0 left, 1 right, 2 top, 3 bottom).
    (tl, target, _element, cfg) => {
      const direction = Math.abs(Math.round(cfg.params.direction ?? 3)) % 4;
      setStatic(tl, target, { revealDir: direction });
      const [vx, vy] = SLIDE_VECTORS[direction] ?? SLIDE_VECTORS[3];
      const d = cfg.params.distance ?? 80;
      tl.fromTo(
        target,
        { dx: vx * d, dy: vy * d, revealProgress: 0 },
        { dx: 0, dy: 0, revealProgress: 1, ...timing(cfg) },
        0,
      );
    },
  ),
];
